import { useState } from 'react';
import { inviteColleagues, createDeviceShare } from '../api';

/**
 * ShareWithColleague — offered on the screen right after a case goes out,
 * because that is the moment the sender has just watched the thing work.
 *
 * One input takes an email OR a mobile number, and the two go down different
 * paths on purpose:
 *
 *   email  → our server sends it, attributed to the sender by name.
 *   mobile → we mint the link and hand the text to the SENDER'S OWN messaging
 *            app via an sms: deep link. Nothing leaves our infrastructure.
 *
 * We do not send the SMS ourselves. A marketing text to a dentist who never
 * consented is TCPA exposure per message, and a colleague relationship does not
 * transfer that consent to us — while a text from a number the recipient
 * already knows is the one most likely to be read anyway.
 *
 * Nothing about the transfer that triggered this is read or sent. There is no
 * free-text note field: a text box next to a case someone just sent is an
 * invitation to paste a patient name into a marketing email.
 */

const STATUS_COPY = {
  sent: (e) => `Invite sent to ${e}`,
  already_member: (e) => `${e} already uses Send`,
  already_invited: (e) => `You've already invited ${e}`,
  invalid: (e) => `${e} doesn't look like an email address`,
  self: () => `That's your own address`,
  undeliverable: (e) => `Couldn't deliver to ${e}`,
  rate_limited: () => `Daily invite limit reached — try again tomorrow`,
  error: (e) => `Something went wrong sending to ${e}`,
};

/** Digits-only-ish and long enough to be a real number. */
function looksLikePhone(v) {
  const digits = v.replace(/[^\d]/g, '');
  return !v.includes('@') && digits.length >= 10 && digits.length <= 15;
}

export default function ShareWithColleague() {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [note, setNote] = useState(null);
  const [err, setErr] = useState(null);

  const trimmed = value.trim();
  const isPhone = trimmed && looksLikePhone(trimmed);
  const canSubmit = !busy && trimmed.length > 3;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    setNote(null);
    try {
      if (isPhone) {
        const r = await createDeviceShare();
        // `?&body=` is the form both iOS and Android accept — iOS wants the
        // separator before body, Android tolerates the empty query.
        const href = `sms:${trimmed.replace(/[^\d+]/g, '')}?&body=${encodeURIComponent(r.message)}`;
        window.location.href = href;
        setNote('Opening your messaging app…');
        setValue('');
      } else {
        const r = await inviteColleagues([trimmed]);
        setResults(r.results || []);
        if ((r.results || []).some((x) => x.status === 'sent')) setValue('');
      }
    } catch (e2) {
      setErr(e2.message || 'Could not share right now');
    } finally {
      setBusy(false);
    }
  }

  async function handleShareSheet() {
    setBusy(true);
    setErr(null);
    setNote(null);
    try {
      const r = await createDeviceShare();
      if (navigator.share) {
        await navigator.share({ title: 'Intake.Dental Send', text: r.message, url: r.url });
      } else {
        await navigator.clipboard.writeText(r.url);
        setNote('Link copied — paste it wherever you like');
      }
    } catch (e2) {
      // An abandoned share sheet throws AbortError; that is not a failure worth
      // showing anyone.
      if (e2?.name !== 'AbortError') setErr(e2.message || 'Could not create a link');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="card"
      style={{ textAlign: 'left', marginTop: 16, background: '#f8fafc' }}
    >
      <h2 style={{ margin: '0 0 4px', fontSize: 17 }}>Know another office that needs this?</h2>
      <p className="muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
        Most offices find Send because a colleague sent them a case. Share it with
        one — we'll never follow up or add them to a list.
      </p>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <input
          type="text"
          inputMode="email"
          autoComplete="off"
          placeholder="Email or mobile number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          style={{ flex: '1 1 220px', minWidth: 0 }}
          aria-label="Colleague's email address or mobile number"
        />
        <button className="btn btn-primary" type="submit" disabled={!canSubmit}>
          {busy ? 'Working…' : isPhone ? 'Text it' : 'Send invite'}
        </button>
      </form>

      {isPhone && (
        <p className="muted" style={{ fontSize: 12.5, margin: '8px 0 0' }}>
          This opens your own messaging app — the text comes from your number, not ours.
        </p>
      )}

      <div style={{ marginTop: 10 }}>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleShareSheet}
          disabled={busy}
          style={{ fontSize: 14 }}
        >
          Copy a link instead
        </button>
      </div>

      {note && (
        <p style={{ margin: '10px 0 0', fontSize: 14, color: '#0c4a6e' }}>{note}</p>
      )}
      {err && (
        <p style={{ margin: '10px 0 0', fontSize: 14, color: '#b91c1c' }}>{err}</p>
      )}
      {results.map((r) => (
        <p
          key={r.email + r.status}
          style={{
            margin: '10px 0 0',
            fontSize: 14,
            color: r.status === 'sent' ? '#047857' : '#92400e',
          }}
        >
          {(STATUS_COPY[r.status] || STATUS_COPY.error)(r.email)}
        </p>
      ))}
    </div>
  );
}
